import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Building2, CalendarClock } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { C, soft } from '@parsina/framework/tokens';
import {
  TextInput, Select, Textarea, Button, Toggle, SkeletonList, PageHeader,
  EmptyState, useToast, phone,
} from '@parsina/framework';
import SegmentedControl from '../../components/common/SegmentedControl';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import AvatarCropModal from '../../components/common/AvatarCropModal';
import JoinClinicPanel from '../../components/JoinClinicPanel.jsx';
import ClinicSwitchButton from '../../components/common/ClinicSwitchButton';
import SettingsCreditsPanel from './SettingsCreditsPanel';
import TemplatesPanel from './web/TemplatesPanel';
import ClinicSiamField, { useClinicSiamIds } from './web/ClinicSiamField';
import { CoinGlyph } from '@parsina/framework/clinical/src/components/common/CoinChip';
import { specialtyLabel } from '../../constants/specialties';
import { useSettingsForm } from './useSettingsForm';
import { useMyClinics } from '@parsina/framework/clinical/src/hooks/useMyClinics';
import ThemePicker from './ThemePicker';
import { SettingsSkeleton } from './SettingsSkeleton';
import FontFamilyPicker from './FontFamilyPicker';
import VisitLayoutEditor from './VisitLayoutEditor';
import UploadFilesSettings from './UploadFilesSettings';
import { useUploadSettings } from './useUploadSettings';
import LanguagePicker from './LanguagePicker';
import { localizedThemePresets, localizedFontSizes } from './appearanceLabels';
import { accountUrl } from '../../lib/runtimeEnv';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { isIranMarket } from '@parsina/framework/ui/lib/brand';
import { visibleSections, isVisibleSection, APP_SECTIONS } from './marketSections';
import OnlineBookingPanel from './web/OnlineBookingPanel';
import { useOnlineBookingStrings } from './onlineBookingStrings';
import {
  SECTIONS, DEFAULT_SECTION,
  THEME_PRESETS, FONT_SIZES,
  VISIT_AI_TOGGLES, DIFFERENTIAL_SUB_TOGGLES, AI_VITAL_ALERT_TOGGLES,
  vitalAlertsArmed, profileChecklist, aiToggleImpactKey,
  DIFFERENTIAL_POLL_INTERVALS, differentialPollSeconds, differentialPollLabel,
  readAvatarFile,
} from '@parsina/framework/clinical/src/screens/Settings/Settings.shared';
import SpeechEngineSetting from '@parsina/framework/clinical/src/screens/Settings/SpeechEngineSetting';
import PrescriptionSettings from '@parsina/framework/clinical/src/screens/Settings/PrescriptionSettings';
import {
  SectionCard, FieldGrid, ToggleGroupBlock, ProfileStatusCard, StickySaveBar,
  ClinicRow, Icon, AccountHandoff, R_CONTROL, R_CARD, SHADOW_CARD, LTR, FORM_MAX,
} from './web/SettingsKit';



// A fixed instant for the formatting preview, so the sample date and the
// «3 days ago» beside it never disagree and never re-render on a timer. The
// pair is what the preview is for: the same moment, in two calendars.
const PREVIEW_INSTANT = new Date('2025-08-03T11:00:00Z');
const PREVIEW_NOW = PREVIEW_INSTANT.getTime() + 3 * 24 * 60 * 60 * 1000;

const TAB_ICONS = {
  profile:    <Icon.user s={17} />,
  ai:         <Icon.sparkles s={17} />,
  visit:      <Icon.layout s={17} />,
  templates:  <Icon.clinic s={17} />,
  prescription: <Icon.rx s={17} />,
  credits:    <CoinGlyph size={16} />,
  language:   <Icon.globe s={17} />,
  appearance: <Icon.palette s={17} />,
  clinics:    <Icon.badge s={17} />,
  booking:    <CalendarClock size={17} strokeWidth={2} aria-hidden />,
  security:   <Icon.lock s={17} />,
};

const SECTION_BY_ID = Object.fromEntries([...SECTIONS, ...APP_SECTIONS].map((sec) => [sec.id, sec]));

/**
 * A profile field, tagged so the completeness rail can focus it by key.
 *
 * The kit inputs take no `id` or `ref` (they render their own <input> and
 * forward neither), so the anchor has to be a wrapper the screen owns. One
 * wrapper per field also gives the rail a scroll target that includes the
 * label, not just the box.
 */
const Field = ({ name, children }) => <div data-field={name}>{children}</div>;

// Largest file we'll even read into memory before cropping. The cropper always
// re-encodes to a small 300×300 JPEG, so this only guards against someone
// picking a huge RAW/PNG and stalling the browser.

/**
 * A row that sends the doctor to حساب پارسینا instead of editing here.
 *
 * The fields it replaces — name, کد ملی, birth date, gender, password — were
 * writable from up to four panels through paths that disagreed with each other:
 * two different name-splitting rules, a کد ملی that was read-only on one patient
 * client and editable on the other, and a password change that existed for
 * doctors and nobody else. One writer ends that; a link alone would not.
 *
 * A plain navigation, not a token hand-off — same realm, so the account app runs
 * its own PKCE flow and Keycloak's SSO session returns a token without a second
 * sign-in. `accountUrl` adds the return_url.
 */
function AccountLinkCard({ t, title, hint, cta, testId }) {
  return (
    <SectionCard
      testId={testId}
      icon={<Icon.user s={20} />} iconBg={soft(C.navy, 0.09)} iconColor={C.navy}
      title={title} subtitle={hint}
    >
      <a
        href={accountUrl('/identity')}
        data-testid={`${testId}-link`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 40,
          padding: '0 18px', borderRadius: 10, background: C.navy, color: '#fff',
          fontSize: '0.84375rem', fontWeight: 700, textDecoration: 'none' }}
      >
        {cta}
      </a>
    </SectionCard>
  );
}

export default function SettingsWeb() {
  const s = useSettingsForm();
  const toast = useToast();
  const { t, has, formatDate, formatTime, formatNumber, formatMoney, formatRelativeTime, dir, locale, locales } = useTranslation();
  // Section names the kit's catalogue may not carry yet («نوبت‌دهی آنلاین»): the
  // kit's wording when it has the key, this app's table otherwise.
  const { t: sectionText } = useOnlineBookingStrings();
  // The kit's rows carry Persian labels; the catalogue keys on their ids.
  const themePresets = localizedThemePresets({ t, has }, THEME_PRESETS);
  const fontSizes = localizedFontSizes({ t, has }, FONT_SIZES);

  // No specialty PICKER any more: the value is the council's, read against the
  // number in `medicalCode` and rendered read-only below. `specialtyLabel`
  // still translates what is stored — a catalogue value shows in the language
  // on screen, and a legacy free-text value renders verbatim.

  const [searchParams, setSearchParams] = useSearchParams();
  const formRef = useRef(null);

  // Avatar: the picked-but-not-yet-cropped source, plus confirm-removal state.
  const [cropSrc, setCropSrc] = useState(null);
  const [confirmRemoveAvatar, setConfirmRemoveAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  // Set once the doctor joins a clinic here — swaps the form for a confirmation
  // so a second submit can't fire.
  const [joinedClinic, setJoinedClinic] = useState(null);

  // Held while a tab switch is waiting on the discard confirmation.
  const [pendingTab, setPendingTab] = useState(null);

  // ── Tab ⇄ URL (§3.2) ───────────────────────────────────────────────────────
  // The section used to be plain component state, so a refresh, a back press or
  // a pasted link all dropped the doctor back on «اطلاعات پزشک». `SECTIONS.id`
  // was always meant to be the URL tab; this is it finally being one.
  const requested = searchParams.get('tab');
  const activeTab = isVisibleSection(requested, 'desktop') ? requested : DEFAULT_SECTION;

  const goToTab = useCallback((id) => {
    const next = new URLSearchParams(searchParams);
    if (id === DEFAULT_SECTION) next.delete('tab'); else next.set('tab', id);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // «فایل‌ها» lives in the visit tab and is read only while that tab is open.
  const uploads = useUploadSettings({ enabled: activeTab === 'visit' });

  // Leaving the profile form — or the files form — with uncommitted edits silently
  // threw them away.
  const onTabChange = useCallback((id) => {
    if (id === activeTab) return;
    if (activeTab === 'profile' && s.dirty) { setPendingTab(id); return; }
    if (activeTab === 'visit' && uploads.dirty) { setPendingTab(id); return; }
    goToTab(id);
  }, [activeTab, s.dirty, uploads.dirty, goToTab]);

  const clinicsOpen = activeTab === 'clinics';
  const myClinics = useMyClinics({ enabled: clinicsOpen });
  // The تأمین SIAM id per clinic — its own query, so a failure costs only the
  // SIAM fields and never the clinic list. تأمین is an Iranian insurer: outside
  // the Iranian market (`market: 'intl'`) the field is not offered, nor asked for.
  const siamOffered = isIranMarket();
  const siamByClinic = useClinicSiamIds({ enabled: clinicsOpen && siamOffered });

  const displayName = useMemo(
    () => [s.form.firstName, s.form.lastName].filter(Boolean).join(' ')
      || s.doctor?.name || t('settings.profile.completeProfile'),
    [s.form.firstName, s.form.lastName, s.doctor, t],
  );

  const checklist = useMemo(() => profileChecklist(s.form), [s.form]);
  const alertsArmed = vitalAlertsArmed(s.ai);

  // The re-assessment controls belong to «اجرای خودکار تشخیص افتراقی»: with the
  // master switch off nothing runs by itself, so a cadence for it would be
  // describing something that never happens.
  const autoDiffArmed = s.ai.autoDifferentialOnStart !== false;
  const pollArmed = autoDiffArmed && s.ai.differentialPollingEnabled !== false;
  // A cadence is read, not calculated: «هر ۲ دقیقه» is the same interval as
  // «هر ۱۲۰ ثانیه» and the doctor should not have to do the division.
  const pollOptions = useMemo(
    () => DIFFERENTIAL_POLL_INTERVALS.map((sec) => ({
      value: String(sec),
      label: differentialPollLabel(sec, t, formatNumber),
    })),
    [t, formatNumber],
  );

  // The rail's gap rows are the way into the field they name.
  const focusField = useCallback((key) => {
    const wrapper = formRef.current?.querySelector(`[data-field="${key}"]`);
    if (!wrapper) return;
    wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
    wrapper.querySelector('input, textarea, select, button')?.focus();
  }, []);

  const onSaveProfile = async () => {
    const res = await s.saveProfile();
    const firstError = res.errors && Object.keys(res.errors)[0];
    if (!res.ok && firstError) focusField(firstError);
  };

  // ── Avatar ─────────────────────────────────────────────────────────────────
  // The validation and the read are shared with the phone (`Settings.shared`):
  // both surfaces must reject the same files with the same message, and a 20MB
  // photo straight off a camera is exactly the case the limit exists for.
  const pickAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    // Clear immediately so re-picking the same file still fires onChange.
    e.target.value = '';
    if (!file) return;

    const res = await readAvatarFile(file);
    if (!res.ok) { toast.error(t(res.messageKey)); return; }
    setCropSrc(res.dataUri);
  };

  const onCropConfirmed = async (dataUri) => {
    const res = await s.saveAvatar(dataUri);
    if (res.ok) setCropSrc(null);
  };

  const onJoined = useCallback((link) => {
    setJoinedClinic(link);
    // The membership list is the confirmation — re-read it rather than splice
    // an optimistic row, so the status shown is the server's (§2.4).
    myClinics.refetch();
  }, [myClinics]);

  const TABS = useMemo(() => visibleSections('desktop').map((sec) => ({
    id: sec.id,
    label: sectionText(sec.labelKey),
    icon: TAB_ICONS[sec.id],
    // The one signal that survives leaving the tab: unsaved profile edits.
    dot: sec.id === 'profile' && s.dirty,
  })), [s.dirty, sectionText]);

  // ── Loading (§2.4) — the page's own skeleton mirrors the P2 layout ─────────
  if (s.loading) {
    return (
      <Page>
        <SettingsSkeleton />
      </Page>
    );
  }

  // ── Error (§2.4) — a failed load must not look like an empty form ──────────
  if (s.error && !s.doctor) {
    return (
      <Page>
        <div data-testid="settings-error" data-state="error">
          <ErrorState
            title={t('settings.profile.accountUnavailable')}
            error={s.error}
            onRetry={s.refetch}
          />
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
        data-testid="settings"
        data-state="content"
      >
        {/* ④ Toolbar — the section switcher lives in one white card row, the
            same zone every other desktop page puts its controls in. The active
            section's hint rides the end edge: `SECTIONS` has carried one per
            section all along and only the mobile nav was rendering it. */}
        <div
          data-testid="settings-toolbar"
          style={{
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: R_CARD, boxShadow: SHADOW_CARD, padding: '10px 12px',
          }}
        >
          <SegmentedControl
            items={TABS}
            value={activeTab}
            onChange={onTabChange}
            ariaLabel={t('settings.sectionsLabel')}
            testId="settings-tabs"
          />
          <span
            data-testid="settings-toolbar-hint"
            style={{ fontSize: '0.75rem', color: C.textMuted, minWidth: 0 }}
          >
            {SECTION_BY_ID[activeTab] && sectionText(SECTION_BY_ID[activeTab].hintKey)}
          </span>
        </div>

        {/* ── اطلاعات پزشک — P2 main + rail (§2.2) ──────────────────────────
            The form used to be a P1 stack of full-width cards, which on a wide
            monitor laid all five identity fields out in a single flat row of
            220px boxes: a banner, not a form. The fields now sit in a capped
            two-column main body, and the space that buys goes to the rail. */}
        {activeTab === 'profile' && (
          <div
            ref={formRef}
            className="grid grid-cols-1 min-[1181px]:grid-cols-[minmax(0,1fr)_19rem] gap-5 items-start"
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18, minWidth: 0 }}>
              {/* The subtitle deliberately says nothing about WHERE these fields
                  are stored — the identity provider is infrastructure, not the
                  doctor's concern. */}
              {/* IDENTITY MOVED. These fields are edited in حساب پارسینا now —
                  one writer instead of four, which is what stops the drift. The
                  professional facts below (نظام پزشکی، تخصص، بیو) stay: they are
                  not "who you are", they are "what you are licensed to do", they
                  live in doctorpreference with their own unique index, and
                  `specialty` decides which app a doctor is routed into. */}
              <AccountLinkCard
                t={t}
                testId="settings-identity"
                title={t('settings.account.accountLinkTitle')}
                hint={t('settings.account.accountLinkHint')}
                cta={t('settings.account.accountLinkIdentity')}
              />

              <SectionCard
                testId="settings-professional"
                icon={<Icon.badge s={20} />} iconBg={soft(C.teal, 0.12)} iconColor={C.teal}
                title={t('settings.profile.professional')} subtitle={t('settings.profile.professionalHint')}
              >
                <FieldGrid max={2}>
                  {/* Locked once stated. The number is not a profile field,
                      it is this account's identity in سازمان نظام پزشکی:
                      re-typing it swaps the council record the account speaks
                      as, and with it the name on prescriptions already signed.
                      The BFF ignores it too — this is the same rule said where
                      the doctor can see it, rather than an edit that is
                      silently dropped on save. Still writable for an account
                      onboarded before the register existed, which holds none. */}
                  <Field name="medicalCode">
                    {s.councilLocked ? (
                      <TextInput
                        label={t('common.labels.medicalCode')}
                        data-testid="settings-medicalCode"
                        value={s.form.medicalCode}
                        onChange={() => {}}
                        disabled
                        inputStyle={LTR}
                        helperText={t('settings.profile.medicalCodeLocked')}
                      />
                    ) : (
                      <TextInput label={t('common.labels.medicalCode')} data-testid="settings-medicalCode" value={s.form.medicalCode} onChange={s.setField('medicalCode')} placeholder={t('settings.profile.medicalCodeExample')} required error={s.errors.medicalCode && t(s.errors.medicalCode)} inputStyle={LTR} />
                    )}
                  </Field>
                  {/* Read-only, and not because a picker would be awkward
                      here: the specialty is whatever سازمان نظام پزشکی holds
                      against the council number, and the doctor does not get to
                      type over it (the BFF rewrites it on every save). */}
                  <Field name="specialty">
                    <TextInput
                      label={t('common.labels.specialty')}
                      data-testid="settings-specialty"
                      value={specialtyLabel(t, s.form.specialty)}
                      onChange={() => {}}
                      disabled
                    />
                  </Field>
                </FieldGrid>
                <div style={{ fontSize: '0.71875rem', color: C.textSecondary, marginTop: 8, lineHeight: 1.8 }}>
                  {t('settings.profile.fromCouncil')}
                </div>
                <Field name="bio">
                  <div style={{ marginTop: 14, maxWidth: FORM_MAX }}>
                    <Textarea label={t('common.labels.bio')} data-testid="settings-bio" value={s.form.bio} onChange={s.setField('bio')} placeholder={t('settings.profile.bioPlaceholder')} rows={4} />
                  </div>
                </Field>
              </SectionCard>

              {/* SaveBar (§6.4): appears only when dirty, and sticks to the
                  bottom of the scroll container — the form is now tall enough
                  that a static bar can be scrolled out of reach while typing. */}
              {s.dirty && (
                <StickySaveBar
                  saving={s.saving}
                  onReset={s.resetProfile}
                  onSave={onSaveProfile}
                />
              )}
            </div>

            {/* Rail — completeness as a checklist you can act on, wrapped
                around the profile picture. */}
            <ProfileStatusCard
              name={displayName}
              specialty={specialtyLabel(t, s.form.specialty)}
              percent={s.completeness}
              items={checklist}
              onJump={focusField}
              avatar={s.doctor?.avatar}
              avatarBusy={s.avatarBusy}
              onPickAvatar={() => avatarInputRef.current?.click()}
              onRemoveAvatar={() => setConfirmRemoveAvatar(true)}
            />

            <input
              ref={avatarInputRef}
              type="file"
              accept="image/jpeg,image/png"
              onChange={pickAvatarFile}
              style={{ display: 'none' }}
              data-testid="settings-avatar-input"
            />
          </div>
        )}

        {/* ── دستیار هوشمند ──────────────────────────────────────────────────
            One card, not two. The old split («صفحه ویزیت» / «هشدارها») mirrored
            the shape of AIAgentSettingsInput rather than anything the doctor
            thinks about, and it stranded the two numeric thresholds in a
            different card from the alarms they tune. */}
        {activeTab === 'ai' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 940 }}>
            <SectionCard
              testId="settings-ai-visit"
              icon={<Icon.sparkles s={20} />} iconBg={soft(C.teal, 0.12)} iconColor={C.teal}
              title={t('settings.ai.inVisit')}
              subtitle={t('settings.immediateLong')}
            >
              <ToggleGroupBlock hint={t('settings.ai.autoRunHint')}>
                {VISIT_AI_TOGGLES.map((toggle) => (
                  <React.Fragment key={toggle.key}>
                    <Toggle
                      id={`settings-ai-${toggle.key}`}
                      data-testid={`settings-ai-${toggle.key}`}
                      label={t(toggle.labelKey)}
                      description={t(aiToggleImpactKey(toggle, s.ai))}
                      checked={s.ai[toggle.key] !== false}
                      onChange={(v) => s.setAiSetting(toggle.key, v)}
                    />

                    {/* The two re-assessments, and the cadence of the timed
                        one, hang off the differential switch the same way the
                        vital thresholds hang off the alarms below: indented
                        under the thing they qualify, and unusable when that
                        thing is off. Rendered as siblings in the flat list they
                        would read as independent automations, which is exactly
                        what they are not — neither runs with the master off. */}
                    {toggle.key === 'autoDifferentialOnStart' && (
                      <div
                        data-testid="settings-ai-differential-polling"
                        style={{
                          marginTop: 12, paddingInlineStart: 14,
                          borderInlineStart: `2px solid ${C.border}`,
                          opacity: autoDiffArmed ? 1 : 0.55,
                          display: 'flex', flexDirection: 'column', gap: 12,
                        }}
                      >
                        {DIFFERENTIAL_SUB_TOGGLES.map((sub) => (
                          <Toggle
                            key={sub.key}
                            id={`settings-ai-${sub.key}`}
                            data-testid={`settings-ai-${sub.key}`}
                            label={t(sub.labelKey)}
                            description={t(sub.impactKey)}
                            checked={s.ai[sub.key] !== false}
                            disabled={!autoDiffArmed}
                            onChange={(v) => s.setAiSetting(sub.key, v)}
                          />
                        ))}
                        <div style={{ maxWidth: 260 }}>
                          <Select
                            label={t('settings.ai.pollInterval')}
                            data-testid="settings-ai-pollInterval"
                            value={String(differentialPollSeconds(s.ai))}
                            options={pollOptions}
                            placeholder=""
                            disabled={!pollArmed}
                            helperText={t('settings.ai.pollIntervalHint')}
                            onChange={(e) => {
                              const n = Number(e.target.value);
                              if (Number.isFinite(n) && n > 0) {
                                s.setAiSetting('differentialPollIntervalSec', n);
                              }
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </ToggleGroupBlock>

              {/* How the doctor's voice becomes text — «ثبت صوتی» and the
                  visit recording. Commits on change like the switches above. */}
              <ToggleGroupBlock
                title={t('settings.ai.speech.title')}
                hint={t('settings.ai.speech.hint')}
                style={{ marginTop: 22 }}
              >
                <SpeechEngineSetting ai={s.ai} onChange={(v) => s.setAiSetting('speechEngine', v)} />
              </ToggleGroupBlock>

              <ToggleGroupBlock
                title={t('settings.ai.vitalAlerts')}
                hint={t('settings.ai.thresholdHint')}
                style={{ marginTop: 22 }}
              >
                {AI_VITAL_ALERT_TOGGLES.map((toggle) => (
                  <Toggle
                    key={toggle.key}
                    id={`settings-ai-${toggle.key}`}
                    data-testid={`settings-ai-${toggle.key}`}
                    label={t(toggle.labelKey)}
                    checked={s.ai[toggle.key] !== false}
                    onChange={(v) => s.setAiSetting(toggle.key, v)}
                  />
                ))}

                {/* The thresholds belong to the two switches above: indented
                    under them, and dimmed to unusable when both are off — an
                    input that cannot affect anything must not look live. */}
                <div style={{
                  marginTop: 14, paddingInlineStart: 14,
                  borderInlineStart: `2px solid ${C.border}`,
                }}>
                  <FieldGrid min={200} max={2}>
                    <div data-testid="settings-ai-spo2">
                      <TextInput
                        label={t('settings.ai.spo2Threshold')}
                        {...s.thresholdField('spo2Threshold')}
                        inputMode="numeric"
                        dir="ltr"
                        disabled={!alertsArmed}
                      />
                    </div>
                    <div data-testid="settings-ai-bp">
                      <TextInput
                        label={t('settings.ai.bpThreshold')}
                        {...s.thresholdField('bpSystolicThreshold')}
                        inputMode="numeric"
                        dir="ltr"
                        disabled={!alertsArmed}
                      />
                    </div>
                  </FieldGrid>
                </div>
              </ToggleGroupBlock>

              {/* No «which suggestions exist» group any more: the clinical
                  assistant no longer suggests tests, imaging or drugs at all —
                  those are ordered in «دستورات» and «نسخه», each with its own
                  AI — so there is nothing here left to switch off. */}
            </SectionCard>
          </div>
        )}

        {/* ── صفحه ویزیت ─────────────────────────────────────────────────────
            What the visit page SHOWS, as opposed to «دستیار هوشمند» (what it
            does by itself). Every control commits on change like the toggles
            above; the editor itself is shared with mobile and holds no state —
            see VisitLayoutEditor. */}
        {activeTab === 'visit' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 940 }}>
            <SectionCard
              testId="settings-visit-layout"
              icon={<Icon.layout s={20} />} iconBg={soft(C.navy, 0.09)} iconColor={C.navy}
              title={t('settings.visit.title')}
              subtitle={t('settings.visit.subtitle')}
            >
              <VisitLayoutEditor
                settings={s.visitLayout}
                onChange={s.setVisitLayout}
                onReset={s.resetVisitLayout}
              />
            </SectionCard>
            {/* «فایل‌ها» — what the doctor's own uploads are held to (a section of this
                tab, not a tab of its own: DICOM design §23.3 A8). A form with a save
                bar, unlike the switches above: a size is typed, not flipped. */}
            <SectionCard
              testId="settings-files-card"
              icon={<Icon.layout s={20} />} iconBg={soft(C.navy, 0.09)} iconColor={C.navy}
              title={t('settings.files.title')}
              subtitle={t('settings.files.subtitle')}
            >
              <UploadFilesSettings s={uploads} />
            </SectionCard>
          </div>
        )}

        {/* ── قالب‌ها ─────────────────────────────────────────────────────────
            The templates a doctor applies during a visit. Self-contained: it
            owns its own reads and writes rather than joining `useSettingsForm`,
            because a template is a ROW the doctor creates and deletes, not a
            field of one settings object that saves as a whole. */}
        {activeTab === 'templates' && <TemplatesPanel />}

        {/* ── نسخه ── what the doctor's visits bill as (the RVU3 visit code):
            the degree the council's register lists, confirmed or corrected
            here, and the تمام‌وقت / fifteen-years switches it cannot know.
            Its own query, so it loads with this tab and fails alone. */}
        {activeTab === 'prescription' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 940 }}>
            <SectionCard
              testId="settings-prescription-card"
              icon={<Icon.rx s={20} />} iconBg={soft(C.navy, 0.10)} iconColor={C.navy}
              title={t('settings.prescription.title')}
              subtitle={t('settings.prescription.subtitle')}
            >
              <PrescriptionSettings />
            </SectionCard>
          </div>
        )}

        {/* ── اعتبار ── */}
        {activeTab === 'credits' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <SettingsCreditsPanel isMobile={false} />
          </div>
        )}

        {/* ── زبان ───────────────────────────────────────────────────────────
            Language gets its own tab rather than a row inside «ظاهر برنامه».
            It is the one setting whose whole purpose is to be findable by
            someone who cannot read the screen it is on, so it must be reachable
            in one look at the tab strip — not two clicks deep behind a label
            they cannot parse. The tag chips in the picker are what make that
            work: «EN» and «FA» read the same in either language.

            It still writes to the appearance SECTION on the wire (that is where
            doctorpreference stores it); only the navigation is separate. */}
        {activeTab === 'language' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 940 }}>
            {/* LANGUAGE STAYS HERE FOR NOW — and this is a dependency, not a
                preference. حساب پارسینا writes Keycloak's `locale`; THIS panel
                reads `doctor_settings.appearance.language` from doctorpreference.
                They are different stores. Removing this picker would leave a
                doctor unable to change their panel language at all, and changing
                it in the account app would not reach this panel.

                The clinic panel already reads `locale`, so its picker CAN move.
                This one moves once the doctor language is migrated to `locale`
                too — see docs/PROMPT-account-redirect-all-panels.md §4. */}
            <SectionCard
              testId="settings-language"
              icon={<Icon.globe s={20} />} iconBg={soft(C.navy, 0.09)} iconColor={C.navy}
              title={t('settings.language.title')}
              subtitle={t('settings.language.subtitle')}
            >
              <LanguagePicker value={s.language} onChange={s.setLanguage} />

              <div style={{
                marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}`,
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                fontSize: '0.75rem', color: C.textMuted,
              }}>
                <strong style={{ color: C.textSecondary, fontWeight: 700 }}>
                  {t('settings.language.direction.title')}:
                </strong>
                <span data-testid="settings-language-direction">
                  {dir === 'rtl'
                    ? t('settings.language.direction.rtl')
                    : t('settings.language.direction.ltr')}
                </span>
                <span>— {t('settings.language.direction.note')}</span>
              </div>
            </SectionCard>

            {/* Formatting is the half of «language» a picker cannot show. Dates
                switch calendar, numbers switch digits, money keeps its toman
                meaning in both — a doctor about to change the language of a
                clinical record is entitled to see that before committing. */}
            <SectionCard
              testId="settings-language-preview"
              icon={<Icon.palette s={20} />} iconBg={soft(C.teal, 0.12)} iconColor={C.teal}
              title={t('settings.language.preview.title')}
            >
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
                gap: 14,
              }}>
                {[
                  ['date', formatDate(PREVIEW_INSTANT, 'full')],
                  ['time', formatTime(PREVIEW_INSTANT)],
                  ['number', formatNumber(1234567)],
                  ['money', `${formatMoney(25000000)} ${t('common.units.toman')}`],
                  ['relative', formatRelativeTime(PREVIEW_INSTANT, PREVIEW_NOW)],
                ].map(([key, value]) => (
                  <div key={key} data-testid={`settings-language-preview-${key}`}>
                    <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginBottom: 4 }}>
                      {t(`settings.language.preview.${key}`)}
                    </div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 700, color: C.textPrimary }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        )}

        {/* ── ظاهر برنامه ── */}
        {activeTab === 'appearance' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <SectionCard
              testId="settings-appearance"
              icon={<Icon.palette s={20} />} iconBg={soft(C.navy, 0.09)} iconColor={C.navy}
              title={t('settings.appearance.themeTitle')}
              subtitle={t('settings.appearance.themeSubtitle')}
            >
              <ThemePicker
                presets={themePresets}
                value={s.appearance.theme}
                onChange={(id) => s.setAppearanceSetting('theme', id)}
              />
            </SectionCard>

            <SectionCard
              testId="settings-fontsize"
              icon={<Icon.palette s={20} />} iconBg={soft(C.teal, 0.12)} iconColor={C.teal}
              title={t('settings.appearance.fontSizeTitle')}
              subtitle={t('settings.appearance.fontSizeSubtitle')}
            >
              <div
                role="radiogroup"
                aria-label={t('settings.appearance.fontSizeTitle')}
                data-testid="settings-fontsize-group"
                style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}
              >
                {fontSizes.map((f) => {
                  const selected = s.appearance.fontSize === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      tabIndex={selected ? 0 : -1}
                      data-testid={`settings-fontsize-${f.id}`}
                      onClick={() => s.setAppearanceSetting('fontSize', f.id)}
                      style={{
                        minHeight: 44, padding: '0 20px', cursor: 'pointer',
                        borderRadius: R_CONTROL,
                        background: selected ? soft(C.navy, 0.06) : C.card,
                        border: `1.5px solid ${selected ? C.navy : C.border}`,
                        color: selected ? C.textPrimary : C.textSecondary,
                        fontWeight: selected ? 700 : 600,
                        fontSize: f.id === 'small' ? '0.8125rem' : f.id === 'large' ? '1.0625rem' : '0.9375rem',
                      }}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </SectionCard>

            <SectionCard
              testId="settings-fontfamily"
              icon={<Icon.palette s={20} />} iconBg={soft(C.navy, 0.09)} iconColor={C.navy}
              title={t('settings.appearance.fontFamilyTitle')}
              subtitle={t('settings.appearance.fontFamilySubtitle')}
            >
              <FontFamilyPicker
                value={s.appearance.fontFamily}
                onChange={(id) => s.setAppearanceSetting('fontFamily', id)}
              />
            </SectionCard>
          </div>
        )}

        {/* ── مطب‌ها ─────────────────────────────────────────────────────────
            The section used to answer only «چطور عضو شوم؟». The more common
            question — «الان عضو کجاها هستم؟» — had no answer anywhere in the
            doctor app, so the memberships lead and the join form follows. */}
        {activeTab === 'clinics' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 940 }}>
            <SectionCard
              testId="settings-my-clinics"
              icon={<Icon.clinic s={20} />} iconBg={soft(C.navy, 0.09)} iconColor={C.navy}
              title={t('settings.clinics.mine')}
              subtitle={t('settings.clinics.mineSubtitle')}
            >
              {myClinics.loading ? (
                <SkeletonList count={2} trailing={72} label={t('common.state.loading')} />
              ) : myClinics.error ? (
                <ErrorState
                  title={t('settings.clinics.unavailable')}
                  error={myClinics.error}
                  onRetry={myClinics.refetch}
                />
              ) : myClinics.clinics.length === 0 ? (
                <EmptyState
                  icon={<Building2 size={44} strokeWidth={1.5} aria-hidden />}
                  title={t('settings.clinics.none')}
                  description={t('settings.clinics.noneHint')}
                />
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
                  gap: 12,
                }}>
                  {myClinics.clinics.map((link) => (
                    <ClinicRow
                      key={link.id}
                      name={link.clinic?.name || t('settings.clinics.unnamed')}
                      /* A clinic's office line is a LANDLINE (021…, 065…, 032…), which
                         fails the kit's Iranian MOBILE pattern. `forDisplay` carries
                         `allowLandline` itself — it IS the read path — so the flag can
                         no longer be forgotten here, and an office number can no longer
                         format to `''` and vanish out of the meta line. */
                      meta={[
                        link.clinic?.city,
                        link.clinic?.officePhone && phone.forDisplay(link.clinic.officePhone),
                      ].filter(Boolean).join(' · ')}
                      status={link.status}
                    >
                      {/* Your تأمین SIAM id AT THIS CLINIC — per membership. Not
                          offered while the membership is still pending. */}
                      {siamOffered && link.status !== 'pending' && siamByClinic?.[link.id] && (
                        <ClinicSiamField
                          clinicId={siamByClinic[link.id].clinicId}
                          value={siamByClinic[link.id].siamId}
                        />
                      )}
                    </ClinicRow>
                  ))}
                </div>
              )}
              {/* Renders only when this account has a clinic panel it may
                  enter; the sidebar carries the same switch for daily use. */}
              <div style={{ marginTop: 14 }}><ClinicSwitchButton /></div>
            </SectionCard>

            <SectionCard
              testId="settings-clinics"
              icon={<Icon.badge s={20} />} iconBg={soft(C.teal, 0.12)} iconColor={C.teal}
              title={t('settings.clinics.join')}
              subtitle={t('settings.clinics.joinHint')}
            >
              {joinedClinic ? (
                <div
                  role="status"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                    background: C.greenLight, border: `1px solid ${soft(C.green, 0.35)}`,
                    borderRadius: R_CONTROL, padding: '12px 14px', color: C.green, fontSize: '0.8125rem',
                  }}
                >
                  <span style={{ flexShrink: 0, display: 'flex' }}><Icon.check s={18} /></span>
                  <span dir="auto" style={{ flex: 1, minWidth: 180 }}>
                    {t('clinic.joinRequested', { name: joinedClinic.clinic?.name || t('common.labels.clinic') })}
                    {joinedClinic.status === 'pending' && t('settings.clinics.pendingSuffix')}
                  </span>
                  {/* Not «پیوستن به مطب دیگر»: that name contains the join
                      button's own name, so a by-name query for the form's
                      button would match this one too. */}
                  <Button variant="ghost" onClick={() => setJoinedClinic(null)} data-testid="settings-clinics-join-again">
                    {t('clinic.addAnother')}
                  </Button>
                </div>
              ) : (
                <div style={{ maxWidth: 440 }}>
                  <JoinClinicPanel showTitle={false} onJoined={onJoined} />
                </div>
              )}
            </SectionCard>
          </div>
        )}

        {/* ── نوبت‌دهی آنلاین (PM-0382) ───────────────────────────────────────
            The doctor's own rules for patients booking themselves, per clinic:
            «توقف نوبت آنلاین», «تا چند روز آینده» and the days off. Its own
            queries, so it loads with this tab and fails alone. */}
        {activeTab === 'booking' && <OnlineBookingPanel onGoToClinics={() => goToTab('clinics')} />}

        {/* ── امنیت ──────────────────────────────────────────────────────────
            Sign-in leads: two instant switches a doctor may flip on any visit,
            above a form they will use once a year. */}
        {activeTab === 'security' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, maxWidth: 940 }}>
            {/* ── SIGN-IN AND PASSWORD BOTH LIVE IN حساب پارسینا ──────────
                The two toggles that used to sit here — «ورود دومرحله‌ای با
                پیامک» and «ورود با اثر انگشت یا چهره» — were DECORATIVE:
                persisted in doctorpreference, rendered, and consumed by nothing.
                No required action, no authenticator, no effect on any sign-in.
                A switch that reports a security state it does not control is
                worse than no switch, so they are gone rather than relabelled.
                The account app's two-factor is the real one — it adds Keycloak's
                own CONFIGURE_TOTP, which Keycloak then enforces. */}
            <AccountHandoff
              testId="settings-password"
              icon={<Icon.lock s={20} />} iconBg={soft(C.navy, 0.09)} iconColor={C.navy}
              title={t('settings.account.handoffSecurityTitle')}
              subtitle={t('settings.account.handoffSecuritySub')}
              items={[
                t('settings.account.handoffSecurityPassword'),
                t('settings.account.handoffSecurityTotp'),
                t('settings.account.handoffSecurityDevices'),
              ]}
              cta={t('settings.account.handoffSecurityCta')}
              href={accountUrl('/security')}
              note={t('settings.account.handoffNote')}
            />
          </div>
        )}
      </div>

      {/* Switching sections used to discard uncommitted profile edits without a
          word. The dot on the tab warns; this catches the case anyway. */}
      <ConfirmDialog
        open={pendingTab !== null}
        onOpenChange={(open) => { if (!open) setPendingTab(null); }}
        title={t('settings.profile.unsavedTitle')}
        message={t('settings.profile.unsavedBody')}
        confirmLabel={t('settings.profile.unsavedLeave')}
        cancelLabel={t('settings.profile.unsavedStay')}
        destructive
        onConfirm={() => {
          const target = pendingTab;
          setPendingTab(null);
          s.resetProfile();
          uploads.reset();
          if (target) goToTab(target);
        }}
      />

      {/* ── Avatar dialogs ── */}
      <AvatarCropModal
        open={!!cropSrc}
        onOpenChange={(open) => { if (!open) setCropSrc(null); }}
        src={cropSrc}
        onCrop={onCropConfirmed}
        busy={s.avatarBusy}
      />

      <ConfirmDialog
        open={confirmRemoveAvatar}
        onOpenChange={setConfirmRemoveAvatar}
        title={t('settings.avatar.removeTitle')}
        message={t('settings.avatar.removeBody')}
        confirmLabel={t('settings.avatar.remove')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={() => {
          setConfirmRemoveAvatar(false);
          s.removeAvatar();
        }}
      />
    </Page>
  );
}

/**
 * The canonical desktop page shell (§2.2), identical to Accounting/Encounters/
 * Credits: a full-bleed `PageHeader` bar, then a padded, full-width body.
 *
 * This replaced a centred `maxWidth: 860` column under a floating gradient hero
 * card. That combination is what made the screen read as a modal sitting on top
 * of the app instead of a page inside it — a narrow centred slab with its own
 * chrome is exactly the shape of a dialog. Settings is a sidebar destination,
 * so §2.2 puts it in the top-level `PageHeader` family: no back affordance, no
 * bespoke bar, same 64px white header bar every other page opens with.
 *
 * The header carries only the title and subtitle. The doctor's name, specialty
 * and avatar are not repeated in its context slot — the profile card below is
 * already the one place that states (and edits) that identity.
 *
 * Padding uses only the two sanctioned breakpoints (§4.1): 16px below 769px,
 * 32px above. The body is deliberately NOT width-capped — cards fill the page
 * like every sibling page, and the field grids inside them add columns as the
 * viewport grows rather than leaving a narrow ribbon of content.
 *
 * What IS capped is the content *inside* a card (`FieldGrid max`, `FORM_MAX`).
 * Uncapped cards plus uncapped fields is what produced a five-across row of
 * 220px inputs at 1600px: the card filling the page is the page rule, a form
 * line running the whole width of a workstation is just a long line.
 */
function Page({ children }) {
  const { t } = useTranslation();
  return (
    // No `direction` here: it is inherited from <html dir>, which I18nProvider
    // owns. Pinning it on a container is what used to make an English UI render
    // its layout right-to-left inside an otherwise left-to-right page.
    <div className="h-full flex flex-col bg-background overflow-y-auto">
      <PageHeader title={t('settings.title')} sub={t('settings.subtitle')} />

      <div className="px-4 min-[769px]:px-8 py-6 flex flex-col gap-5">
        {children}
      </div>
    </div>
  );
}
